'use client';
import { useState, useCallback } from 'react';

export interface Preset<T> {
  id: string;
  name: string;
  settings: T;
  createdAt: number;
}

function getStorageKey(toolId: string) {
  return `audio_presets_${toolId}`;
}

function loadPresets<T>(toolId: string): Preset<T>[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(getStorageKey(toolId));
    if (!raw) return [];
    return JSON.parse(raw) as Preset<T>[];
  } catch {
    return [];
  }
}

function savePresetsToStorage<T>(toolId: string, presets: Preset<T>[]) {
  try {
    localStorage.setItem(getStorageKey(toolId), JSON.stringify(presets));
  } catch {
    // storage full or unavailable
  }
}

export function usePresets<T>(toolId: string) {
  const [presets, setPresets] = useState<Preset<T>[]>(() => loadPresets<T>(toolId));

  const savePreset = useCallback((name: string, settings: T) => {
    const newPreset: Preset<T> = {
      id: `${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      name: name.trim(),
      settings,
      createdAt: Date.now(),
    };
    setPresets(prev => {
      const updated = [...prev, newPreset];
      savePresetsToStorage(toolId, updated);
      return updated;
    });
    return newPreset;
  }, [toolId]);

  const deletePreset = useCallback((id: string) => {
    setPresets(prev => {
      const updated = prev.filter(p => p.id !== id);
      savePresetsToStorage(toolId, updated);
      return updated;
    });
  }, [toolId]);

  const renamePreset = useCallback((id: string, newName: string) => {
    setPresets(prev => {
      const updated = prev.map(p => p.id === id ? { ...p, name: newName.trim() } : p);
      savePresetsToStorage(toolId, updated);
      return updated;
    });
  }, [toolId]);

  return { presets, savePreset, deletePreset, renamePreset };
}
